import {
  forwardRef,
  type Ref,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Space, type TableColumnsType, theme } from 'antd';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { searchFilterJobs } from 'pos-client/components/scheduleBoard/utils/searchFilterJobs.ts';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { PosInputSearch } from 'pos-client/components/shared/Form/Search/PosSearchInput.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';

type SlotInformationType = {
  laneId: string;
  date: Date;
};

export type AddNewJobModalRef = {
  openModal: (laneId: string, date: Date) => void;
};

const AddNewJobModal = (_: object, ref: Ref<AddNewJobModalRef>) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [open, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<string>();
  const [slotData, setSlotData] = useState<SlotInformationType>();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { boardData, setSelectedTimeSlot, setSelectedLaneId } =
    useScheduleBoardContext();

  const filteredBoardData = useMemo(() => {
    return boardData?.filter((item) => {
      const slot = item as ScheduleBoardJobModel;
      if (!slot.orderId) return false;

      if (
        slot.state.toLowerCase() !== 'cancelled' &&
        slot.state.toLowerCase() !== 'complete'
      ) {
        return true;
      }
    }) as ScheduleBoardJobModel[];
  }, [boardData]);

  const alphabetizedBoardData = useMemo(() => {
    return [...(filteredBoardData ?? [])].sort((a, b) => {
      const customerAName = `${a.customer.firstName} ${a.customer.lastName}`;
      const customerBName = `${b.customer.firstName} ${b.customer.lastName}`;

      return customerAName.localeCompare(customerBName);
    });
  }, [filteredBoardData]);

  const filteredSearchTermBoardData = useMemo(() => {
    return searchFilterJobs(alphabetizedBoardData, searchTerm);
  }, [alphabetizedBoardData, searchTerm]);

  const tableData = useTableData(filteredSearchTermBoardData, (x) => x.jobId);
  const columns: TableColumnsType<ScheduleBoardJobModel> = [
    {
      title: t('orders.orderReference'),
      dataIndex: 'orderReference',
      key: 'orderReference',
      render: (_, record) => (
        <Link
          to={`/orders/manage-orders/${record.orderId}/summary`}
          target="_blank"
        >
          {record?.orderReference}
        </Link>
      ),
    },
    {
      title: t('customers.customer'),
      dataIndex: 'customer',
      key: 'customer',
      render: (_, record) => (
        <>
          {record?.customer?.firstName} {record?.customer?.lastName}
        </>
      ),
    },
    {
      title: t('common.vehicle.vehicle'),
      dataIndex: 'fulfilmentTo',
      key: 'fulfilmentTo',
      render: (_, record) => record?.fulfilmentTo?.reference,
    },
    {
      title: '',
      key: 'orderId',
      render: (_, record) => {
        const isSelected = record.orderId === selectedOrder;

        return (
          <Button
            type={isSelected ? 'primary' : 'default'}
            onClick={() => setSelectedOrder(record.orderId)}
            style={{ width: '100%' }}
          >
            {isSelected
              ? t('common.buttons.selected')
              : t('common.buttons.select')}
          </Button>
        );
      },
      width: 100,
    },
  ];

  const handleClose = () => {
    setSelectedOrder(undefined);
    setModalOpen(false);
    setSearchTerm('');
  };

  const handleCreateJob = () => {
    if (slotData) {
      setSelectedTimeSlot(slotData.date);
      setSelectedLaneId(slotData.laneId);
      navigate(`/orders/manage-orders/${selectedOrder}/jobs`);
    }
  };

  useImperativeHandle(ref, () => {
    return {
      openModal: (laneId, date) => {
        setSlotData({ laneId, date });
        setModalOpen(true);
      },
    };
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <PosModal
      header={{
        title: t('jobs.createNewJob'),
        style: { width: '50%' },
        children: (
          <PosInputSearch
            allowClear
            onChange={handleSearch}
            placeholder={t('common.placeholders.searchBy', {
              searchBy: t(
                'scheduling.scheduleBoard.customerNameOrderReferenceOrLicensePlate'
              ),
            })}
          />
        ),
      }}
      open={open}
      onCancel={handleClose}
      width={token.screenXL}
      footer={
        <Space>
          <Button onClick={handleClose}>{t('common.buttons.close')}</Button>
          <Button
            type="primary"
            disabled={!selectedOrder || !slotData}
            onClick={handleCreateJob}
            style={{ marginLeft: 'auto' }}
          >
            {t('jobs.createNewJob')}
          </Button>
        </Space>
      }
    >
      <PosTable
        dataSource={tableData}
        columns={columns}
        isLoading={!boardData}
        noDataProps={{
          description: t('scheduling.scheduleBoard.noJobsFound'),
        }}
      />
    </PosModal>
  );
};

export default forwardRef(AddNewJobModal);
